// @flow
import bugsnag from '@bugsnag/js'
import BugsnagPluginReact from '@bugsnag/plugin-react'
import * as React from 'react'
import { isDev, isLocal, isServer, isStaging, version } from '../env'
import { currentUser } from './firebaseAuth'

type BugsnagCallbackEventType = {
  setUser: (userId: string, email: string, name?: string) => void
}
export function createErrorBoundary (): any {
  if (isDev || isLocal()) {
    return (class ErrorBoundary extends React.Component<{children: React.Node}> {
      render (): React.Node {
        return this.props.children
      }
    })
  } else {
    const bugsnagClient = bugsnag.start({
      apiKey: 'b61387ebfd348ea3d8fe068deae9a57b',
      collectUserIp: false,
      enabledReleaseStages: ['production', 'staging'],
      releaseStage: isStaging() ? 'staging' : 'production',
      appVersion: version,
      plugins: [new BugsnagPluginReact(React)],
      onError: (event: BugsnagCallbackEventType): void => {
        const user = currentUser()
        if (user && user.id && user.email && !isServer) {
          event.setUser(user.id, user.email)
        }
      }
    })

    return bugsnagClient.getPlugin('react')
  }
}

export function reportError (e: Error, onError?: (event: any) => void): void {
  bugsnag.notify(e, onError)
}

export function extractErrorDetails (error: any, method: string, url: string, startTime: number): any {
  const duration = Date.now() - startTime
  const baseDetails = {
    method,
    url,
    duration,
    timestamp: new Date().toISOString(),
    environment: process.env.NEXT_PUBLIC_APP_ENV || 'production',
    userAgent: typeof window !== 'undefined' ? window.navigator.userAgent : 'server'
  }

  // Network error (no response received)
  if (error.request && !error.response) {
    return {
      ...baseDetails,
      type: 'network_error',
      message: error.message || 'Network Error',
      code: error.code || 'ERR_NETWORK',
      requestDetails: {
        readyState: error.request.readyState,
        status: error.request.status,
        statusText: error.request.statusText,
        responseURL: error.request.responseURL
      }
    }
  }

  // Response error (server responded with error status)
  if (error.response) {
    return {
      ...baseDetails,
      type: 'response_error',
      message: `${error.response.status} ${error.response.statusText}`,
      code: error.code,
      response: {
        status: error.response.status,
        statusText: error.response.statusText,
        data: error.response.data,
        headers: error.response.headers
      }
    }
  }

  // Request setup error
  if (error.message && !error.request) {
    return {
      ...baseDetails,
      type: 'request_error',
      message: error.message,
      code: error.code,
      stack: error.stack
    }
  }

  // Unknown error
  return {
    ...baseDetails,
    type: 'unknown_error',
    message: error.message || 'Unknown error occurred',
    code: error.code,
    error: String(error)
  }
}
